import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import * as SvgIcons from 'assets/svg/sprite'
import AddToPlaylist from 'components/UI/Modal/AddToPlaylist/AddToPlaylist'
import { PostOptionsDropdown } from 'components-ref/molecules/post/OptionsDropdown/PostOptionsDropdown'
import { useModalContext } from 'context/modalContext'
import { type IFile } from 'types/interfaces/IFile'
import { type IPost } from 'types/interfaces/ITypes'

export interface PostRendererOptionsHandle {
  open: () => void
  close: () => void
}

interface Props {
  role: string
  postId: string
  postData: IPost
  allFiles: IFile[]
  removeFromHistoryCb?: () => void
  onDelete: () => void
  onPin: () => void
  onOpenStats: () => void
}

export const PostRendererOptions = forwardRef<PostRendererOptionsHandle, Props>(
  (
    {
      role,
      postId,
      postData,
      allFiles,
      removeFromHistoryCb,
      onDelete,
      onPin,
      onOpenStats,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const modalContext = useModalContext()
    const [isOptionsNavOpen, setIsOptionsNavOpen] = useState(false)

    useImperativeHandle(
      ref,
      () => ({
        open: () => setIsOptionsNavOpen(true),
        close: () => setIsOptionsNavOpen(false),
      }),
      []
    )

    const optionsArray = useMemo(() => {
      const close = () => setIsOptionsNavOpen(false)

    const baseOptions = [
    // {
    //   icon: <SvgIcons.IconOptionsCopy />,
    //   text: t('copyLinkToPost'),
    //   disabled: false,
    //   action: () => {
    //     navigator.clipboard
    //       .writeText(`${window.location.origin}/post/${postId}`)
    //       .then(() => {
    //         close()
    //         addToast('success', t('urlCopiedToClipboard'))
    //       })
    //   },
    // },
    ...(removeFromHistoryCb
        ? [
            {
            icon: <SvgIcons.IconOptionsStats />,
            text: t('removeFromWatchHistory'),
            disabled: false,
            action: () => {
                close()
                removeFromHistoryCb()
            },
            },
        ]
        : []),
    ]

      if (role === 'owner') {
        return [
          {
            icon: <SvgIcons.IconOptionsEdit />,
            text: t('editPost'),
            disabled: false,
            action: () => {
              close()

              navigate(`/edit/post/${postId}`, {
                state: {
                  editPost: postData,
                },
              })
            },
          },
          {
            icon: <SvgIcons.IconOptionsPin />,
            text: postData.is_pinned ? t('unpinPost') : t('pinPost'),
            disabled: false,
            action: () => {
              close()
              onPin()
            },
          },
          {
            icon: <SvgIcons.IconOptionsStats />,
            text: t('viewStats'),
            disabled: false,
            action: () => {
              close()
              onOpenStats()
            },
          },
          ...baseOptions,
        ]
      }

      return [
        {
          icon: <SvgIcons.IconOptionsPlaylist />,
          text: t('addToList'),
          disabled: allFiles.length === 0,
          action: () => {
            close()
            modalContext.addModal(
              '',
                          <AddToPlaylist
                            itemId={postId}
                            itemType='post'
                            closeFn={() => modalContext.clearModal()}
                          />,
              true,
              true
            )
          },
        },
        ...baseOptions,
      ]
    }, [
      role,
      postId,
      postData.is_pinned,
      allFiles.length,
      removeFromHistoryCb,
      t,
      navigate,
      modalContext,
      onPin,
      onOpenStats,
    ])

    const handleTriggerClick = () => {
      setTimeout(() => {
        setIsOptionsNavOpen(true)
      }, 0)
    }

    return (
      <>
        <PostRendererOptionsNav
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={handleTriggerClick}
        >
          <SvgIcons.IconThreeDots />
        </PostRendererOptionsNav>

        <PostOptionsDropdown
          role={role}
          content="post"
          isOptionsNavOpen={isOptionsNavOpen}
          setIsOptionsNavOpen={setIsOptionsNavOpen}
          postId={postId}
          options={optionsArray}
          deleteFn={onDelete}
        />
      </>
    )
  }
)

PostRendererOptions.displayName = 'PostRendererOptions'

const PostRendererOptionsNav = styled.div`
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  background: rgba(0, 0, 0, 0.3);
  z-index: 5;
  padding: 0.6rem;
  display: flex;
  border-radius: 2rem;
  cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    user-select: none;
    -webkit-user-select: none;
`